import React from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';
import App from './App';
import theme from './theme';

// Global config object that will be populated from the server
window.APP_CONFIG = {};

// Fetch client configuration from the server
async function fetchConfig() {
  try {
    // Determine the base URL dynamically
    const baseUrl = window.location.origin;
    const configEndpoint = `${baseUrl}/api/client-config`;
    
    console.log('Fetching client configuration from:', configEndpoint);
    const response = await axios.get(configEndpoint);
    window.APP_CONFIG = response.data;
    
    console.log('Client configuration loaded successfully');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch client configuration:', error);
    
    // Fallback to any environment variables that might be available
    // This handles local development where env vars might be set
    window.APP_CONFIG = {
      REACT_APP_API_URL: process.env.REACT_APP_API_URL || '/api',
      REACT_APP_AZURE_AD_CLIENT_ID: process.env.REACT_APP_AZURE_AD_CLIENT_ID,
      REACT_APP_AZURE_AD_TENANT_ID: process.env.REACT_APP_AZURE_AD_TENANT_ID,
      REACT_APP_AZURE_AD_REDIRECT_URI: process.env.REACT_APP_AZURE_AD_REDIRECT_URI || window.location.origin,
      REACT_APP_AZURE_AD_AUTHORITY: process.env.REACT_APP_AZURE_AD_AUTHORITY,
      REACT_APP_API_SCOPE: process.env.REACT_APP_API_SCOPE
    };
    
    return window.APP_CONFIG;
  }
}

// Initialize the application
async function initializeApp() {
  try {
    // First fetch the configuration
    const config = await fetchConfig();
    
    // Create MSAL configuration using the fetched config
    const msalConfig = {
      auth: {
        clientId: config.REACT_APP_AZURE_AD_CLIENT_ID,
        authority: config.REACT_APP_AZURE_AD_AUTHORITY,
        redirectUri: config.REACT_APP_AZURE_AD_REDIRECT_URI || window.location.origin,
        navigateToLoginRequestUrl: true,
        postLogoutRedirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
      system: {
        loggerOptions: {
          loggerCallback: (level, message, containsPii) => {
            if (!containsPii) console.log(message);
          },
          logLevel: "Verbose", 
        }
      }
    };

    // Initialize MSAL with the configuration
    const msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
    
    // Handle any redirect after initialization and get response
    const response = await msalInstance.handleRedirectPromise();
    
    // If we have a response from a redirect, we can use the account from there
    if (response && response.account) {
      console.log('Redirect response received with account - setting active account');
      msalInstance.setActiveAccount(response.account);
      
      // If we're currently on the login page and there's a valid account, redirect to home
      if (window.location.pathname === '/login') {
        console.log('Redirecting from login page to home after authentication');
        window.location.replace(window.location.origin);
        return; // Stop execution - we're redirecting
      }
    } 
    // Otherwise check if there are any accounts in the cache
    else if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
      console.log('Found accounts in cache - setting first account as active');
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
      
      // If we're currently on the login page and there's a valid account in cache, redirect to home
      if (window.location.pathname === '/login') {
        console.log('Redirecting from login page to home with cached account');
        window.location.replace(window.location.origin);
        return; // Stop execution - we're redirecting
      }
    }
    
    // Only render the app after MSAL is initialized
    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(
      <React.StrictMode>
        <MsalProvider instance={msalInstance}>
          <BrowserRouter>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <App />
            </ThemeProvider>
          </BrowserRouter>
        </MsalProvider>
      </React.StrictMode>
    );
  } catch (error) {
    console.error("Failed to initialize app:", error);
    // Render an error state
    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(
      <div style={{ padding: 20, textAlign: 'center' }}>
        <h1>Application Error</h1>
        <p>There was a problem initializing the application.</p>
        <p>Please refresh the page or try again later.</p>
        <p style={{ color: 'red', marginTop: 20 }}>Error: {error.message}</p>
      </div>
    );
  }
}

// Start the application initialization
initializeApp();